import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getDeleteEmspUserOcpiTokenDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/delete-emsp-user-ocpi-token.form-default-values';
import { DeleteEmspUserOcpiTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/delete-emsp-user-ocpi-token.model';
import React, { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useEmspBoDeleteOcpiTokenForEmspUserMutation } from '@app/core/service/emsp-bo-emsp-user.service';

import {
  DeleteEmspBoEmspUserOcpiTokenContextProvider,
  useEmspBoEmspUserOcpiTokenContext,
} from '../emsp-bo-emsp-user.context';
import { EmspBoEmspUserOcpiTokenDeleteFormController } from './emsp-bo-emsp-user-ocpi-token-delete-form/emsp-bo-emsp-user-ocpi-token-delete-form.controller';

interface EmspBoEmspUserOcpiTokenDeleteControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const EmspBoEmspUserOcpiTokenDeleteController = ({
  onCancel,
  onSuccess,
}: EmspBoEmspUserOcpiTokenDeleteControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [deleteOcpiTokenForEmspUser] =
    useEmspBoDeleteOcpiTokenForEmspUserMutation();
  const ocpiToken = useEmspBoEmspUserOcpiTokenContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<DeleteEmspUserOcpiTokenModel> = (
    errors,
  ) => {
    Logger.error(errors);
    toast.error(
      t('$.emsp-bo-emsp-user-ocpi-token-delete.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    DeleteEmspUserOcpiTokenModel
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);

    deleteOcpiTokenForEmspUser(fieldValues)
      .unwrap()
      .then(() => {
        toast.success(t('$.emsp-bo-emsp-user-ocpi-token-delete.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.emsp-bo-emsp-user-ocpi-token-delete.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getDeleteEmspUserOcpiTokenDefaultValues(
    ocpiToken.userId,
    ocpiToken.id,
  );

  return (
    <DeleteEmspBoEmspUserOcpiTokenContextProvider data={data}>
      <EmspBoEmspUserOcpiTokenDeleteFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </DeleteEmspBoEmspUserOcpiTokenContextProvider>
  );
};
